import { supabase } from '../lib/supabase';
import type { Session } from '@supabase/auth-js';
import { gespeicherteSitzung } from '../lib/auth';
import { netz } from './netz';
import type { components } from './schema.gen';

/** Typen aus dem OpenAPI-Schema des Backends (npm run api:typen). */
export type Ich = components['schemas']['Ich'];
export type AppStatus = components['schemas']['AppStatus'];
export type RezeptKarte = components['schemas']['RezeptKarte'];
export type RezeptDetail = components['schemas']['RezeptDetail'];
export type Zutat = components['schemas']['Zutat'];
export type Naehrwert = components['schemas']['Naehrwert'];
export type Bild = components['schemas']['Bild'];
export type Kollektion = components['schemas']['Kollektion'];
export type KollektionMitRezepten = components['schemas']['KollektionMitRezepten'];
export type PlanEintrag = components['schemas']['PlanEintrag'];
export type EinkaufRezept = components['schemas']['EinkaufRezept'];
export type Info = components['schemas']['Info'];
export type RezeptKurz = components['schemas']['RezeptKurz'];
export type Woche = components['schemas']['Woche'];
export type WochenEintrag = components['schemas']['WochenEintrag'];
export type EinkaufListe = components['schemas']['EinkaufListe'];
export type EinkaufPosten = components['schemas']['EinkaufPosten'];
export type EinkaufGruppe = components['schemas']['EinkaufGruppe'];
export type EinkaufRezeptVoll = components['schemas']['EinkaufRezeptVoll'];
export type EinkaufKategorie = components['schemas']['Kategorie'];
export type RezeptEingabe = components['schemas']['RezeptEingabe'];
export type RezeptAenderung = components['schemas']['RezeptAenderung'];
export type ZutatEingabe = components['schemas']['ZutatEingabe'];
export type BildqueueStatus = components['schemas']['BildqueueStatus'];
export type BildAuftragAntwort = components['schemas']['BildAuftragAntwort'];

export class ApiFehler extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
  ) {
    super(`${status}: ${detail}`);
    this.name = 'ApiFehler';
  }
}

function detailAus(daten: unknown): string {
  if (typeof daten === 'object' && daten !== null && 'detail' in daten) {
    const d = (daten as { detail: unknown }).detail;
    if (typeof d === 'string') return d;
    // 422 von FastAPI: Liste von Fehlern, die erste Meldung reicht ("Value error, ..." ohne Praefix)
    if (Array.isArray(d) && d.length > 0) {
      const erste: unknown = d[0];
      if (typeof erste === 'object' && erste !== null && 'msg' in erste) {
        const msg = (erste as { msg: unknown }).msg;
        if (typeof msg === 'string') return msg.replace(/^Value error, /, '');
      }
    }
  }
  return 'Unbekannter Fehler';
}

/**
 * Ruft die eigene API (/api/v1/...) mit dem aktuellen Zugriffstoken auf.
 * Bei 401 wird einmal das Token erneuert und wiederholt, danach abgemeldet.
 */
/** Ohne Netz nicht auf eine Token-Erneuerung warten (auth-js versucht es mit Pausen), sondern das gespeicherte
 * Token nehmen; der Service Worker antwortet dann aus dem Cache. Mit Netz hoechstens 3 s warten. */
async function sitzung(): Promise<{ session: Session | null }> {
  // Zuletzt kein Netz: nicht erst eine Token-Erneuerung abwarten, der Service Worker antwortet ohnehin aus dem Cache
  if (navigator.onLine === false || netz.istOhne()) return { session: gespeicherteSitzung() };
  const zeit = new Promise<{ session: Session | null }>((r) => setTimeout(() => r({ session: gespeicherteSitzung() }), 3000));
  const echt = supabase.auth
    .getSession()
    .then(({ data }) => ({ session: data.session ?? gespeicherteSitzung() }))
    .catch(() => ({ session: gespeicherteSitzung() }));
  return Promise.race([echt, zeit]);
}

export async function api<T>(pfad: string, optionen: RequestInit = {}, wiederholt = false): Promise<T> {
  const data = await sitzung();
  const kopf = new Headers(optionen.headers);
  kopf.set('Accept', 'application/json');
  if (data.session) kopf.set('Authorization', `Bearer ${data.session.access_token}`);
  if (optionen.body && !kopf.has('Content-Type')) kopf.set('Content-Type', 'application/json');

  let antwort: Response;
  try {
    antwort = await fetch(`/api/v1${pfad}`, { ...optionen, headers: kopf });
  } catch (f) {
    netz.fehler();
    throw f;
  }
  if (antwort.headers.get('X-Nebenbei-Cache') === '1') netz.ausCache();
  else netz.erreicht();
  if (antwort.status === 401 && !wiederholt && data.session) {
    const { error } = await supabase.auth.refreshSession();
    if (!error) return api<T>(pfad, optionen, true);
    await supabase.auth.signOut();
  }
  if (antwort.status === 204) return null as T;
  const text = await antwort.text();
  const daten: unknown = text ? JSON.parse(text) : null;
  if (!antwort.ok) throw new ApiFehler(antwort.status, detailAus(daten));
  return daten as T;
}
